// Lightweight markdown → TipTap JSON converter for the Draft Canvas.
//
// Used by the AI Chat "Write to canvas" mode and the per-message "Send to
// Draft Canvas" action. Intentionally minimal — supports the subset the chat
// copilot actually produces:
//   - ATX headings (#..######)
//   - blockquotes (>)
//   - fenced code blocks (``` and ~~~)
//   - unordered lists (- * +) and ordered lists (1. 2.)
//   - paragraphs with inline **bold**, *italic*, `code`, and [text](url) links
//
// Anything outside this set falls through as plain text in a paragraph so
// nothing is ever silently dropped.

type Mark = { type: string; attrs?: Record<string, unknown> };
type Inline = { type: "text"; text: string; marks?: Mark[] };
type Block = { type: string; attrs?: Record<string, unknown>; content?: unknown[] };

const INLINE_TOKEN = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`|\[[^\]]+\]\([^)\s]+\))/g;

function inlineToTextNodes(input: string): Inline[] {
  if (!input) return [];
  const out: Inline[] = [];
  let last = 0;
  const push = (text: string, marks?: Mark[]) => {
    if (!text) return;
    out.push(marks ? { type: "text", text, marks } : { type: "text", text });
  };
  for (const m of input.matchAll(INLINE_TOKEN)) {
    const idx = m.index ?? 0;
    if (idx > last) push(input.slice(last, idx));
    const tok = m[0];
    if (tok.startsWith("**")) push(tok.slice(2, -2), [{ type: "bold" }]);
    else if (tok.startsWith("*")) push(tok.slice(1, -1), [{ type: "italic" }]);
    else if (tok.startsWith("`")) push(tok.slice(1, -1), [{ type: "code" }]);
    else if (tok.startsWith("[")) {
      const close = tok.indexOf("](");
      const text = tok.slice(1, close);
      const href = tok.slice(close + 2, -1);
      push(text, [{ type: "link", attrs: { href } }]);
    }
    last = idx + tok.length;
  }
  if (last < input.length) push(input.slice(last));
  return out;
}

const para = (text: string): Block => {
  const nodes = inlineToTextNodes(text);
  return nodes.length ? { type: "paragraph", content: nodes } : { type: "paragraph" };
};

export function markdownToTiptapDoc(input: string): Block {
  const src = (input ?? "").replace(/\r\n/g, "\n");
  const lines = src.split("\n");
  const blocks: Block[] = [];
  let i = 0;

  const flushParagraph = (buf: string[]) => {
    const joined = buf.join("\n").trim();
    if (joined) blocks.push(para(joined));
    buf.length = 0;
  };

  const paraBuf: string[] = [];

  while (i < lines.length) {
    const line = lines[i];

    // Fenced code block
    const fence = /^([`~]{3,})\s*(\S*)\s*$/.exec(line);
    if (fence) {
      flushParagraph(paraBuf);
      const marker = fence[1][0].repeat(fence[1].length);
      const language = fence[2] || null;
      i++;
      const codeLines: string[] = [];
      while (i < lines.length && !lines[i].startsWith(marker)) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // consume closing fence
      blocks.push({
        type: "codeBlock",
        attrs: language ? { language } : undefined,
        content: codeLines.length ? [{ type: "text", text: codeLines.join("\n") }] : undefined,
      });
      continue;
    }

    // Blank line
    if (line.trim() === "") {
      flushParagraph(paraBuf);
      i++;
      continue;
    }

    // Heading
    const heading = /^(#{1,6})\s+(.+)$/.exec(line);
    if (heading) {
      flushParagraph(paraBuf);
      const level = heading[1].length;
      blocks.push({ type: "heading", attrs: { level }, content: inlineToTextNodes(heading[2]) });
      i++;
      continue;
    }

    // Blockquote (consecutive)
    if (/^>\s?/.test(line)) {
      flushParagraph(paraBuf);
      const quoteLines: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i])) {
        quoteLines.push(lines[i].replace(/^>\s?/, ""));
        i++;
      }
      blocks.push({
        type: "blockquote",
        content: [para(quoteLines.join("\n"))],
      });
      continue;
    }

    // Lists
    const ul = /^[-*+]\s+(.+)$/.exec(line);
    const ol = /^(\d+)\.\s+(.+)$/.exec(line);
    if (ul || ol) {
      flushParagraph(paraBuf);
      const ordered = !!ol;
      const items: Block[] = [];
      while (i < lines.length) {
        const u = /^[-*+]\s+(.+)$/.exec(lines[i]);
        const o = /^(\d+)\.\s+(.+)$/.exec(lines[i]);
        if (ordered && o) items.push({ type: "listItem", content: [para(o[2])] });
        else if (!ordered && u) items.push({ type: "listItem", content: [para(u[1])] });
        else break;
        i++;
      }
      blocks.push({ type: ordered ? "orderedList" : "bulletList", content: items });
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|_{3,}|\*{3,})\s*$/.test(line)) {
      flushParagraph(paraBuf);
      blocks.push({ type: "horizontalRule" });
      i++;
      continue;
    }

    // Default: paragraph buffer
    paraBuf.push(line);
    i++;
  }
  flushParagraph(paraBuf);

  return {
    type: "doc",
    content: blocks.length ? blocks : [{ type: "paragraph" }],
  };
}
